import { useCallback, useEffect } from 'react';
import { SaveOptions } from './types';
import { useImpressionEditorContext } from './Context';
import { ViewMode } from '../Attachments/InlineAttachment';

const useKeyboardShortcut = (handler: (e: KeyboardEvent) => void) =>
  useEffect(() => {
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handler]);

export const useEditorKeyboardShortcuts = (
  onSave: (saveOptions?: SaveOptions) => void,
) => {
  const { isEditing, setIsEditing } = useImpressionEditorContext();
  return useKeyboardShortcut(
    useCallback(
      (e: KeyboardEvent) => {
        if (isEditing) {
          // Save
          if (e.code === 'KeyS' && (e.altKey || e.metaKey)) {
            onSave({ andExitEdit: !e.shiftKey, andNotify: true });
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          return; // don't intercept other keyboard events when editing
        }
        // Enter edit mode
        if (e.code === 'KeyE' && (e.altKey || e.metaKey)) {
          setIsEditing(true);
          e.preventDefault();
          e.stopPropagation();
        }
      },
      [isEditing, onSave, setIsEditing],
    ),
  );
};

export const usePresentationKeyboardShortcuts = (
  openPresentationView: (viewMode: ViewMode) => void,
) =>
  useKeyboardShortcut(
    useCallback(
      (e: KeyboardEvent) => {
        if (e.code === 'KeyP' && (e.altKey || e.metaKey)) {
          if (e.shiftKey) {
            openPresentationView?.('print');
          } else {
            openPresentationView?.('presentation-compact');
          }
          e.preventDefault();
          e.stopPropagation();
        }
      },
      [openPresentationView],
    ),
  );
